import DeleteIcon from '@assets/icons/delete.svg';
import i18n from '@/utils/i18n';
import React, { createRef, useEffect, useMemo, useRef, useState } from 'react';
import {
  BASE_SORT_ORDERS,
  GUEST_MODE_SORT_ORDERS,
  SEARCH_SORT_ORDERS,
  SearchSortOrder,
  useUserContext,
  useUserSettingsContext,
} from '@/contexts/user-context';
import {
  CatalogCategoryType,
  ItemSortOrder,
} from '@/contexts/item-context-types';
import { CatalogFilter, ListDisplayMode } from '@/enums';
import { CLEAR_FILTERS_BUTTON_SIZE } from '@/styles';
import { createStyles } from '@/contexts/theme-context';
import { DisplayModeActionSheet } from './display-mode-action-sheet';
import {
  FlatList,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import {
  isInCategoryItemGroups,
  SearchFilterDisplayStrategy,
  SearchFilterOption,
  SearchFilters,
  transformItemGroupToFilterOption,
} from '@/utils/search-filters';
import { ListViewToggle } from '@/components/list-view-toggle';
import { PillButton } from '@/components/pill-button';
import { SearchFiltersActionSheet } from './search-filters-action-sheet';
import { SortOrderActionSheet } from '@/components/orders/sort-order-action-sheet';
import { sortOrderTranslationMap } from '@/components/orders/sort-order-select-list';
import {
  useDynamicConfigValue,
  useSupplierDynamicConfigFlag,
  useSupplierDynamicConfigValue,
} from '@/hooks/use-dynamic-config-value';
import { useLayoutContext } from '@/contexts/layout-context';
import { useShowListView } from '@/hooks/use-show-list-view';

const DISPLAY_MODE_FILTER_KEY = 'DISPLAY_MODE';

export interface Props {
  category?: CatalogCategoryType;
  filters: SearchFilters;
  mainViewWrapperStyle?: StyleProp<ViewStyle>;
  onChangeSortOrder?: (sortOrder: SearchSortOrder) => void;
  showDisplayModePill?: boolean;
  showSortOrder?: boolean;
  sortOrder?: SearchSortOrder;
}

export const SearchFilterPills: React.FC<Props> = ({
  category,
  filters,
  onChangeSortOrder,
  showDisplayModePill = true,
  showSortOrder = true,
  sortOrder,
  mainViewWrapperStyle,
}: Props) => {
  const { isSmallLayout, isSmallTabletLayout } = useLayoutContext();
  const [styles, theme] = useStyles();

  const [filterSheetVisible, setFilterSheetVisible] = useState(false);
  const [filterOption, setFilterOption] =
    useState<SearchFilterOption<string> | null>(null);
  const [sortOrderSheetVisible, setSortOrderSheetVisible] = useState(false);
  const [displayModeSheetVisible, setDisplayModeSheetVisible] = useState(false);

  const { isGuestMode } = useUserContext();
  const {
    catalogSortOrder,
    largeItemListDisplayMode,
    setCatalogSortOrder,
    setLargeItemListDisplayMode,
  } = useUserSettingsContext();
  const { showListViewToggle } = useShowListView();
  const cardViewV2Enabled = useSupplierDynamicConfigValue(
    'card_view_v2_enabled',
    false,
  );

  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');
  const subcategoriesFilterEnabled =
    useDynamicConfigValue('search_config')?.subcategories_filter_enabled;

  const {
    badges,
    gtinFilters,
    searchFilters,
    badgesFilter,
    setBadgesFilter,
    setSubcategoriesFilter,
    subcategories,
    subcategoriesFilter,
  } = filters;

  const pillButtonRefs = useRef(
    new Map(
      [
        ...Object.values(CatalogFilter),
        DISPLAY_MODE_FILTER_KEY,
        ...searchFilters.filters.map((searchFilter) => searchFilter.key),
      ].map((filter) => [filter, createRef<View>()]),
    ),
  ).current;

  // if any of the subcategories in the filter are not in the item group categories array
  // the search no longer includes that subcategory. So remove the selected subcategory filter
  useEffect(() => {
    if (itemGroupsEnabled && subcategoriesFilterEnabled) {
      setSubcategoriesFilter((prevFilter) => {
        if (!prevFilter || prevFilter.size === 0) {
          return prevFilter;
        }

        const filtersExist = isInCategoryItemGroups(subcategories, prevFilter);

        if (!filtersExist) {
          return new Set();
        }

        return prevFilter;
      });
    }
  }, [
    setSubcategoriesFilter,
    subcategories,
    itemGroupsEnabled,
    subcategoriesFilterEnabled,
  ]);

  const clearFilters = () => {
    setFilterOption(null);
    setBadgesFilter(new Set());
    setSubcategoriesFilter(new Set());
    gtinFilters.resetFilters();
    searchFilters.resetFilters();
  };

  const onClose = () => {
    setFilterOption(null);
    setFilterSheetVisible(false);
  };

  const onSelect = (
    key: CatalogFilter | string,
    selectedValues: Set<string>,
  ) => {
    onClose();

    switch (key) {
      case CatalogFilter.BADGE: {
        setBadgesFilter(selectedValues);
        return;
      }
      case CatalogFilter.SUBCATEGORY: {
        setSubcategoriesFilter(selectedValues);
        return;
      }
      default: {
        const gtinFilter = gtinFilters.listFilters.find(
          (filter) => filter.key === key,
        );
        const searchFilter = searchFilters.filters.find(
          (filter) => filter.key === key,
        );
        if (gtinFilter) {
          gtinFilter.updateFilter(selectedValues);
        }
        if (searchFilter) {
          searchFilter.updateFilter(selectedValues);
        }
        return;
      }
    }
  };

  // When Typesense search filtering is enabled, badge is handled as a search
  // filter (like availability, OG, etc.) instead of the FE badge pill.
  const searchFilteringEnabled = useSupplierDynamicConfigFlag(
    'typesense_config',
    'filtering_enabled',
  );
  const badgeFiltersAvailable =
    !searchFilteringEnabled &&
    itemGroupsEnabled &&
    badges.length > 0 &&
    category;
  const subcategoryFiltersAvailable =
    itemGroupsEnabled &&
    subcategoriesFilterEnabled &&
    subcategories.some((s) => Boolean(s.uuid)) &&
    category;
  const filtersAvailable = useMemo(() => {
    return (
      gtinFilters.listFilters.some((f) => f.isAvailable) ||
      gtinFilters.flagFilters.some((f) => f.isAvailable) ||
      badgeFiltersAvailable ||
      subcategoryFiltersAvailable ||
      searchFilters.filters.some((f) => f.filter.size > 0) ||
      showSortOrder
    );
  }, [
    gtinFilters,
    showSortOrder,
    badgeFiltersAvailable,
    subcategoryFiltersAvailable,
    searchFilters,
  ]);

  const isClearableFilters =
    gtinFilters.listFilters.some((f) => f.filter.size > 0) ||
    gtinFilters.flagFilters.some((f) => f.filterApplied) ||
    badgesFilter.size > 0 ||
    subcategoriesFilter?.size ||
    searchFilters.filters.some((f) => f.filter.size > 0);
  const shouldShowMobileDisplayMode =
    showDisplayModePill &&
    cardViewV2Enabled &&
    (isSmallLayout || isSmallTabletLayout);
  const displayModeLabel =
    largeItemListDisplayMode === ListDisplayMode.List
      ? i18n.t('catalog_display_list')
      : i18n.t('catalog_display_grid');

  const filterButtons = useMemo(() => {
    const buttons = [];

    for (const listFilter of gtinFilters.listFilters) {
      if (listFilter.isAvailable) {
        buttons.push({
          active: listFilter.filter?.size > 0,
          count: listFilter.filter?.size,
          dropDown: true,
          label: listFilter.label,
          onPress: () => {
            setFilterOption({
              key: listFilter.key,
              options: listFilter.options.map((o) => ({
                title: o.title,
                value: String(o.value),
              })),
              selected: listFilter.filter,
              title: listFilter.label,
            });
            setFilterSheetVisible(true);
          },
          ref: pillButtonRefs.get(listFilter.key),
        });
      }
    }

    for (const flagFilter of gtinFilters.flagFilters) {
      if (flagFilter.isAvailable) {
        buttons.push({
          active: flagFilter.filterApplied,
          dropDown: false,
          label: flagFilter.label,
          onPress: () => flagFilter.updateFilter(!flagFilter.filterApplied),
        });
      }
    }

    for (const searchFilter of searchFilters.filters) {
      if (searchFilter.isAvailable) {
        const isActive = searchFilter.filter.size > 0;

        switch (searchFilter.displayStrategy) {
          case SearchFilterDisplayStrategy.TOGGLE: {
            buttons.push({
              active: isActive,
              count: undefined,
              dropDown: false,
              label: searchFilter.label,
              onPress: () => {
                if (isActive) {
                  // Deselect all values
                  searchFilter.updateFilter(new Set());
                } else {
                  // Select all available values
                  const allValues = new Set(
                    searchFilter.options.map((o) => o.value),
                  );
                  searchFilter.updateFilter(allValues);
                }
              },
              ref: pillButtonRefs.get(searchFilter.key),
            });
            break;
          }
          case SearchFilterDisplayStrategy.DROPDOWN: {
            buttons.push({
              active: isActive,
              count: searchFilter.filter.size,
              dropDown: true,
              label: searchFilter.label,
              onPress: () => {
                setFilterOption({
                  key: searchFilter.key,
                  options: searchFilter.options,
                  selected: searchFilter.filter,
                  title: searchFilter.label,
                });
                setFilterSheetVisible(true);
              },
              ref: pillButtonRefs.get(searchFilter.key),
            });
            break;
          }
          default: {
            break;
          }
        }
      }
    }

    if (subcategoryFiltersAvailable) {
      buttons.push({
        active: subcategoriesFilter?.size > 0,
        count: subcategoriesFilter?.size,
        dropDown: true,
        hasLeftDivider: true,
        label: i18n.t('subcategory'),
        onPress: () => {
          setFilterOption({
            key: CatalogFilter.SUBCATEGORY,
            options: transformItemGroupToFilterOption(subcategories),
            selected: subcategoriesFilter,
            title: `${category.name} ${i18n.t('subcategory')}`,
          });
          setFilterSheetVisible(true);
        },
        ref: pillButtonRefs.get(CatalogFilter.SUBCATEGORY),
      });
    }

    if (badgeFiltersAvailable) {
      buttons.push({
        active: badgesFilter.size > 0,
        count: badgesFilter.size,
        dropDown: true,
        label: i18n.t('badges'),
        onPress: () => {
          setFilterOption({
            key: CatalogFilter.BADGE,
            options: badges.map((b) => ({
              title: b.name,
              value: b.uuid,
            })),
            selected: badgesFilter,
            title: i18n.t('badges'),
          });
          setFilterSheetVisible(true);
        },
        ref: pillButtonRefs.get(CatalogFilter.BADGE),
      });
    }

    if (showSortOrder) {
      buttons.push({
        active: false,
        dropDown: true,
        label: i18n.t('sort_order', {
          order: sortOrderTranslationMap[sortOrder || catalogSortOrder],
        }),
        onPress: () => {
          setSortOrderSheetVisible(true);
        },
        ref: pillButtonRefs.get(CatalogFilter.SORT_ORDER),
      });
    }

    if (shouldShowMobileDisplayMode) {
      buttons.push({
        active: false,
        dropDown: true,
        label: i18n.t('catalog_display_pill', { mode: displayModeLabel }),
        onPress: () => {
          setDisplayModeSheetVisible(true);
        },
        ref: pillButtonRefs.get(DISPLAY_MODE_FILTER_KEY),
      });
    }

    return buttons;
  }, [
    badgeFiltersAvailable,
    badges,
    badgesFilter,
    subcategoryFiltersAvailable,
    searchFilters,
    showSortOrder,
    pillButtonRefs,
    subcategoriesFilter,
    category,
    subcategories,
    gtinFilters.listFilters,
    gtinFilters.flagFilters,
    sortOrder,
    catalogSortOrder,
    shouldShowMobileDisplayMode,
    displayModeLabel,
  ]);

  if (!filtersAvailable) {
    return null;
  }

  return (
    <View
      style={[
        !isSmallTabletLayout && !isSmallLayout ? styles.mainWrapper : null,
        mainViewWrapperStyle,
      ]}
    >
      <FlatList
        contentContainerStyle={[
          styles.filtersWrapper,
          isSmallTabletLayout && styles.filtersWrapperTablet,
        ]}
        data={filterButtons}
        horizontal
        keyExtractor={(item) => item.label}
        ListHeaderComponent={
          isClearableFilters ? (
            <TouchableOpacity
              onPress={clearFilters}
              style={styles.clearButton}
              testID="clear-filters"
            >
              <DeleteIcon fill={theme.colors.primary} height={12} />
            </TouchableOpacity>
          ) : null
        }
        renderItem={({ item }) => (
          <>
            {item.hasLeftDivider ? <View style={styles.divider} /> : null}
            <PillButton
              button={item}
              key={item.label}
              ref={item.ref}
              style={styles.pillButton}
            />
          </>
        )}
        showsHorizontalScrollIndicator={false}
      />
      {showListViewToggle ? <ListViewToggle /> : null}
      <DisplayModeActionSheet
        buttonRef={pillButtonRefs.get(DISPLAY_MODE_FILTER_KEY)}
        isVisible={displayModeSheetVisible}
        onChange={setLargeItemListDisplayMode}
        onClose={() => setDisplayModeSheetVisible(false)}
        value={largeItemListDisplayMode}
      />
      {filterOption ? (
        <SearchFiltersActionSheet<string>
          buttonRef={pillButtonRefs.get(filterOption.key)}
          filter={filterOption}
          isVisible={filterSheetVisible}
          onClose={onClose}
          onSelect={onSelect}
        />
      ) : null}
      {sortOrder && onChangeSortOrder ? (
        <SortOrderActionSheet<SearchSortOrder>
          buttonRef={pillButtonRefs.get(CatalogFilter.SORT_ORDER)}
          isVisible={sortOrderSheetVisible}
          onChange={onChangeSortOrder}
          onClose={() => setSortOrderSheetVisible(false)}
          supportedSortOptions={SEARCH_SORT_ORDERS}
          value={sortOrder}
        />
      ) : (
        <SortOrderActionSheet<ItemSortOrder>
          buttonRef={pillButtonRefs.get(CatalogFilter.SORT_ORDER)}
          isVisible={sortOrderSheetVisible}
          onChange={(sortOrder) => setCatalogSortOrder(sortOrder)}
          onClose={() => setSortOrderSheetVisible(false)}
          supportedSortOptions={
            isGuestMode ? GUEST_MODE_SORT_ORDERS : BASE_SORT_ORDERS
          }
          value={catalogSortOrder}
        />
      )}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  clearButton: {
    alignItems: 'center',
    backgroundColor: theme.colors.background,
    borderRadius: CLEAR_FILTERS_BUTTON_SIZE,
    height: CLEAR_FILTERS_BUTTON_SIZE,
    justifyContent: 'center',
    marginRight: theme.sk.spacing.spaceSmall,
    width: CLEAR_FILTERS_BUTTON_SIZE,
  },
  divider: {
    backgroundColor: theme.sk.color.bg.tertiary.default,
    height: CLEAR_FILTERS_BUTTON_SIZE,
    marginRight: theme.sk.spacing.spaceSmall,
    width: 1,
  },
  filtersWrapper: {
    flexDirection: 'row',
    paddingLeft: theme.sk.spacing.spaceMedium,
    paddingRight: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  filtersWrapperTablet: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  mainWrapper: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingRight: theme.sk.spacing.spaceMedium,
  },
  pillButton: {
    marginRight: theme.sk.spacing.spaceSmall,
  },
}));
